// src/pages/Dashboard.jsx
import { useEffect, useState, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  Smartphone, Users as UsersIcon, Gift, CreditCard, GraduationCap,
  Wallet, UserPlus, Eye, EyeOff, ShieldCheck, Copy, ArrowRight,
  ChevronRight, Plus, Users2, Send,
} from "lucide-react";
import api from "../api/api";
import { toast } from "react-toastify";
import Currency from "../components/Currency";
import CurrencyToggle from "../components/CurrencyToggle";
import NewAccountNotice from "../components/NewAccountNotice";
import { useWallet } from "../context/WalletContext";
import { withRetry } from "../utils/retry";

// ── Flowing gradient waves behind the wallet card. Pure SVG, no image file.
function WalletBackgroundWaves({ className = "" }) {
  return (
    <svg
      className={`absolute inset-0 w-full h-full ${className}`}
      viewBox="0 0 800 300"
      preserveAspectRatio="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <defs>
        <linearGradient id="waveA" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#a78bfa" />
          <stop offset="100%" stopColor="#4c1d95" />
        </linearGradient>
        <linearGradient id="waveB" x1="0%" y1="100%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#6d28d9" />
          <stop offset="100%" stopColor="#312e81" />
        </linearGradient>
        <radialGradient id="waveGlow" cx="72%" cy="35%" r="55%">
          <stop offset="0%" stopColor="#e9d5ff" stopOpacity="0.35" />
          <stop offset="100%" stopColor="#e9d5ff" stopOpacity="0" />
        </radialGradient>
      </defs>

      <path
        d="M0,190 C120,130 260,235 420,175 C560,122 660,205 800,150 L800,300 L0,300 Z"
        fill="url(#waveA)"
        opacity="0.55"
      />
      <path
        d="M0,230 C160,170 320,260 480,205 C630,155 720,215 800,190 L800,300 L0,300 Z"
        fill="url(#waveB)"
        opacity="0.6"
      />
      <ellipse cx="580" cy="90" rx="260" ry="150" fill="url(#waveGlow)" />
    </svg>
  );
}

// ── Wallet + coins illustration, pure SVG so it renders crisply at any size.
function WalletIllustration({ className = "" }) {
  return (
    <div className={`pointer-events-none select-none ${className}`} aria-hidden="true">
      <style>{`
        @keyframes wallet-coin-float {
          0%, 100% { transform: translateY(0) rotate(0deg); }
          50%      { transform: translateY(-8px) rotate(4deg); }
        }
        @keyframes wallet-coin-float-slow {
          0%, 100% { transform: translateY(0) rotate(0deg); }
          50%      { transform: translateY(-5px) rotate(-3deg); }
        }
        @keyframes wallet-sparkle-twinkle {
          0%, 100% { opacity: 0.25; transform: scale(0.85); }
          50%      { opacity: 1;    transform: scale(1.05); }
        }
      `}</style>
      <svg
        viewBox="0 0 300 240"
        xmlns="http://www.w3.org/2000/svg"
        className="w-full h-full drop-shadow-2xl"
      >
        <defs>
          <radialGradient id="wGroundShadow" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#1e0a4d" stopOpacity="0.55" />
            <stop offset="100%" stopColor="#1e0a4d" stopOpacity="0" />
          </radialGradient>
          <linearGradient id="wBody" x1="10%" y1="0%" x2="95%" y2="100%">
            <stop offset="0%" stopColor="#e9d5ff" />
            <stop offset="30%" stopColor="#a78bfa" />
            <stop offset="70%" stopColor="#7c3aed" />
            <stop offset="100%" stopColor="#4c1d95" />
          </linearGradient>
          <linearGradient id="wSide" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#4c1d95" />
            <stop offset="100%" stopColor="#2e1065" />
          </linearGradient>
          <linearGradient id="wFlap" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#faf5ff" />
            <stop offset="50%" stopColor="#c4b5fd" />
            <stop offset="100%" stopColor="#8b5cf6" />
          </linearGradient>
          <linearGradient id="wFlapSide" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#8b5cf6" />
            <stop offset="100%" stopColor="#5b21b6" />
          </linearGradient>
          <linearGradient id="wStud" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#fffbeb" />
            <stop offset="45%" stopColor="#fbbf24" />
            <stop offset="100%" stopColor="#92400e" />
          </linearGradient>
          <linearGradient id="wCoinRim" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#f59e0b" />
            <stop offset="100%" stopColor="#92400e" />
          </linearGradient>
          <radialGradient id="wCoinFace" cx="35%" cy="30%" r="75%">
            <stop offset="0%" stopColor="#fffbeb" />
            <stop offset="40%" stopColor="#fde68a" />
            <stop offset="75%" stopColor="#f59e0b" />
            <stop offset="100%" stopColor="#b45309" />
          </radialGradient>
          <filter id="wGlow" x="-100%" y="-100%" width="300%" height="300%">
            <feGaussianBlur stdDeviation="4.5" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
          <filter id="wBigBlur" x="-100%" y="-100%" width="300%" height="300%">
            <feGaussianBlur stdDeviation="16" />
          </filter>
          <filter id="wFlareBlur" x="-200%" y="-200%" width="500%" height="500%">
            <feGaussianBlur stdDeviation="3" />
          </filter>
        </defs>

        <ellipse cx="165" cy="212" rx="95" ry="16" fill="url(#wGroundShadow)" filter="url(#wBigBlur)" />

        <g style={{ animation: "wallet-coin-float-slow 4.5s ease-in-out infinite", transformOrigin: "88px 62px" }}>
          <ellipse cx="88" cy="68" rx="22" ry="22" fill="url(#wCoinRim)" filter="url(#wGlow)" />
          <ellipse cx="88" cy="62" rx="22" ry="22" fill="url(#wCoinFace)" stroke="#92400e" strokeWidth="1" />
          <circle cx="88" cy="62" r="16" fill="none" stroke="#fde68a" strokeOpacity="0.6" strokeWidth="1" />
          <ellipse cx="80" cy="54" rx="7" ry="4.5" fill="#fffbeb" opacity="0.85" />
          <text x="88" y="68" textAnchor="middle" fontSize="17" fontWeight="800" fill="#78350f">₦</text>
        </g>

        <path d="M212 96 L228 108 L228 196 L212 208 Z" fill="url(#wSide)" />
        <rect x="52" y="96" width="160" height="112" rx="20" fill="url(#wBody)" stroke="#3b0764" strokeWidth="2" />
        <path d="M212 96 L228 108 L228 128 L212 116 Z" fill="url(#wFlapSide)" />
        <path d="M52 116 Q132 62 212 116 L212 96 Q132 46 52 96 Z" fill="url(#wFlap)" stroke="#5b21b6" strokeWidth="1.5" />

        <path d="M64 106 L108 106 L74 198 L60 198 Z" fill="#ffffff" opacity="0.16" />
        <path d="M118 106 L130 106 L100 198 L90 198 Z" fill="#ffffff" opacity="0.1" />

        <rect x="62" y="106" width="140" height="92" rx="13" fill="none" stroke="#f5f3ff" strokeOpacity="0.28" strokeWidth="1.4" strokeDasharray="3 4" />

        <circle cx="176" cy="150" r="10.5" fill="url(#wStud)" stroke="#78350f" strokeWidth="1.2" />
        <circle cx="173" cy="147" r="3.2" fill="#fffbeb" opacity="0.9" />

        <g style={{ animation: "wallet-coin-float 3.2s ease-in-out infinite", transformOrigin: "244px 78px" }}>
          <ellipse cx="244" cy="84" rx="24" ry="24" fill="url(#wCoinRim)" filter="url(#wGlow)" />
          <ellipse cx="244" cy="77" rx="24" ry="24" fill="url(#wCoinFace)" stroke="#92400e" strokeWidth="1" />
          <circle cx="244" cy="77" r="17.5" fill="none" stroke="#fde68a" strokeOpacity="0.6" strokeWidth="1" />
          <ellipse cx="235" cy="68" rx="8" ry="5" fill="#fffbeb" opacity="0.85" />
          <text x="244" y="84" textAnchor="middle" fontSize="19" fontWeight="800" fill="#78350f">₦</text>
        </g>

        <circle cx="252" cy="60" r="10" fill="#ffffff" opacity="0.5" filter="url(#wFlareBlur)" />
        <circle cx="70" cy="190" r="8" fill="#fcd34d" opacity="0.4" filter="url(#wFlareBlur)" />

        <g style={{ animation: "wallet-sparkle-twinkle 2.4s ease-in-out infinite" }}>
          <path d="M158 34 L161 42 L169 45 L161 48 L158 56 L155 48 L147 45 L155 42 Z" fill="#ffffff" />
        </g>
        <g style={{ animation: "wallet-sparkle-twinkle 3.1s ease-in-out infinite 0.6s" }}>
          <path d="M40 100 L42 105 L47 107 L42 109 L40 114 L38 109 L33 107 L38 105 Z" fill="#ffffff" />
        </g>
        <g style={{ animation: "wallet-sparkle-twinkle 2.8s ease-in-out infinite 1.1s" }}>
          <circle cx="272" cy="140" r="2.4" fill="#ffffff" />
        </g>
        <circle cx="118" cy="42" r="2" fill="#ffffff" opacity="0.7" />
        <circle cx="264" cy="176" r="1.8" fill="#ffffff" opacity="0.6" />
      </svg>
    </div>
  );
}

// ── One row on mobile's compact service list.
function ServiceRow({ s }) {
  const Icon = s.icon;
  const body = (
    <div
      className={`flex items-center gap-2.5 rounded-lg bg-white dark:bg-[#12101f] border-l-4 ${s.borderColor} border-t border-r border-b border-gray-100 dark:border-gray-800 px-3 py-2.5`}
    >
      <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${s.iconBg}`}>
        <Icon size={16} className={s.iconText} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-[12.5px] font-semibold text-gray-900 dark:text-white leading-tight truncate">{s.title}</p>
        <p className="text-[10.5px] text-gray-500 dark:text-gray-400 leading-tight truncate">{s.desc}</p>
      </div>
      {s.comingSoon ? (
        <span className="flex-shrink-0 text-[8.5px] font-bold px-1.5 py-1 rounded-full bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400 uppercase tracking-wide">
          Soon
        </span>
      ) : (
        <ChevronRight size={15} className="text-gray-300 dark:text-gray-600 flex-shrink-0" />
      )}
    </div>
  );
  return s.comingSoon ? (
    <div className="opacity-70 cursor-not-allowed select-none">{body}</div>
  ) : (
    <Link to={s.to} className="block">{body}</Link>
  );
}

// ── One card on desktop's Quick Access grid.
function ServiceCard({ s }) {
  const Icon = s.icon;
  const body = (
    <div
      className={`relative flex flex-col rounded-xl border ${s.cardBorder} bg-white dark:bg-[#12101f] p-4 h-full transition hover:shadow-sm`}
    >
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center mb-3 ${s.iconBg}`}>
        <Icon size={20} className={s.iconText} />
      </div>
      <p className="text-sm font-semibold text-gray-900 dark:text-white mb-1">{s.title}</p>
      <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed flex-1">{s.desc}</p>
      <div className="mt-3 flex justify-end">
        {s.comingSoon ? (
          <span className="text-[10px] font-bold px-2.5 py-1 rounded-full bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400 uppercase tracking-wide">
            Coming Soon
          </span>
        ) : (
          <ArrowRight size={16} className={s.iconText} />
        )}
      </div>
    </div>
  );
  return s.comingSoon ? (
    <div className="opacity-70 cursor-not-allowed select-none h-full">{body}</div>
  ) : (
    <Link to={s.to} className="block h-full">{body}</Link>
  );
}

export default function Dashboard() {
  const navigate = useNavigate();
  const isLoggingOut = useRef(false);
  const { walletBalance, walletLoading } = useWallet();

  const [balanceVisible, setBalanceVisible] = useState(true);
  const [referral, setReferral] = useState(null);
  const [referralLoading, setReferralLoading] = useState(true);

  const safeLogout = () => {
    if (isLoggingOut.current) return;
    isLoggingOut.current = true;
    localStorage.removeItem("accessToken");
    localStorage.removeItem("refreshToken");
    navigate("/login");
  };

  useEffect(() => {
    const token = localStorage.getItem("accessToken");
    if (!token) {
      navigate("/login");
      return;
    }

    const checkAuth = async () => {
      try {
        await withRetry(() => api.get("/user/me"));
      } catch (err) {
        if (err.response?.status === 401) {
          toast.error("Session expired. Please login again.");
          safeLogout();
        }
      }
    };

    checkAuth();
    const interval = setInterval(checkAuth, 60000);
    return () => clearInterval(interval);
  }, [navigate]);

  // ── Pull the real referral link/code so the dashboard's Refer & Earn
  // strip isn't showing placeholder data — same endpoint Referrals.jsx uses.
  useEffect(() => {
    const loadReferral = async () => {
      try {
        const res = await api.get("/referral/my-referral");
        if (res.data?.success) setReferral(res.data);
      } catch {
        // non-critical widget — fail silently, banner just won't show a link
      } finally {
        setReferralLoading(false);
      }
    };
    loadReferral();
  }, []);

  const copy = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied!`);
  };

  const services = [
    {
      to: "/dashboard/usa-numbers",
      icon: Smartphone,
      title: "Buy Numbers",
      desc: "Virtual numbers for SMS verification.",
      borderColor: "border-l-purple-500",
      cardBorder: "border-purple-200 dark:border-purple-800",
      iconBg: "bg-purple-100 dark:bg-purple-900/40",
      iconText: "text-purple-600 dark:text-purple-300",
    },
    {
      to: "/dashboard/social-logs",
      icon: UsersIcon,
      title: "Social Media Accounts",
      desc: "Verified accounts for all major platforms.",
      borderColor: "border-l-emerald-500",
      cardBorder: "border-emerald-200 dark:border-emerald-800",
      iconBg: "bg-emerald-100 dark:bg-emerald-900/40",
      iconText: "text-emerald-600 dark:text-emerald-300",
    },
    {
      to: "/dashboard/gifts",
      icon: Gift,
      title: "Send Gifts Worldwide",
      desc: "Send thoughtful gifts to anyone, anywhere.",
      borderColor: "border-l-pink-500",
      cardBorder: "border-pink-200 dark:border-pink-800",
      iconBg: "bg-pink-100 dark:bg-pink-900/40",
      iconText: "text-pink-600 dark:text-pink-300",
    },
    {
      icon: CreditCard,
         to: "/dashboard/gift-cards",
         title: "Gift Cards",
      desc: "Top brands. Instant delivery. Best rates.",
      borderColor: "border-l-indigo-400",
      cardBorder: "border-indigo-200 dark:border-indigo-800",
      iconBg: "bg-indigo-100 dark:bg-indigo-900/40",
      iconText: "text-indigo-600 dark:text-indigo-300",
      comingSoon: false,
    },
    {
      to: "/dashboard/tutorials",
      icon: GraduationCap,
      title: "Tutorials",
      desc: "Step-by-step guides to get started.",
      borderColor: "border-l-amber-500",
      cardBorder: "border-amber-200 dark:border-amber-800",
      iconBg: "bg-amber-100 dark:bg-amber-900/40",
      iconText: "text-amber-600 dark:text-amber-300",
    },
  ];

  return (
    // WebkitTextSizeAdjust stops iOS Safari auto-inflating font sizes on
    // rotation, which is what was making the mobile cards feel oversized.
    <div className="animate-fadeIn" style={{ WebkitTextSizeAdjust: "100%" }}>
      <NewAccountNotice />

      {/* ══════════════════ MOBILE WALLET HERO ══════════════════ */}
      <div className="md:hidden relative overflow-hidden rounded-2xl bg-gradient-to-br from-purple-600 to-purple-800 dark:from-purple-700 dark:to-purple-950 p-4 mb-4 shadow-lg">
        <WalletBackgroundWaves className="opacity-90" />
        <WalletIllustration className="absolute right-0 top-1/2 -translate-y-1/2 w-24 h-24 opacity-95" />

        <div className="relative z-10">
          <div className="flex items-center gap-1.5 mb-1">
            <p className="text-[10px] text-purple-200 uppercase tracking-wide font-medium">Wallet Balance</p>
            <button onClick={() => setBalanceVisible((v) => !v)} className="text-purple-200">
              {balanceVisible ? <Eye size={12} /> : <EyeOff size={12} />}
            </button>
          </div>

          {walletLoading ? (
            <div className="h-6 w-28 rounded bg-white/15 animate-pulse mb-2.5" />
          ) : (
            <p className="text-[20px] font-bold text-white mb-2.5 max-w-[62%] break-words leading-tight">
              {balanceVisible ? <Currency amount={walletBalance} /> : "•••••••"}
            </p>
          )}

          <div className="flex items-center gap-1.5 mb-2.5">
            <CurrencyToggle onDark className="scale-[0.85] origin-left" />

            {/* enticing active badge — pulsing dot + soft glow */}
            <div className="flex items-center gap-1 bg-gradient-to-r from-emerald-400 to-emerald-500 rounded-full pl-1.5 pr-2 py-1 shadow-[0_0_10px_rgba(52,211,153,0.55)]">
              <span className="relative flex h-1.5 w-1.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75" />
                <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-white" />
              </span>
              <span className="text-[9px] font-bold text-white tracking-wide">ACTIVE</span>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-1.5 max-w-[66%]">
            <Link
              to="/dashboard/wallet"
              className="inline-flex items-center gap-1 bg-white text-purple-700 text-[11px] font-semibold px-3 py-1.5 rounded-lg hover:bg-purple-50 transition"
            >
              Fund Wallet <ArrowRight size={11} />
            </Link>
            <Link
              to="/dashboard/transfer"
              className="inline-flex items-center gap-1 bg-white/15 text-white text-[11px] font-semibold px-3 py-1.5 rounded-lg hover:bg-white/25 transition"
            >
              <Send size={11} /> Transfer
            </Link>
          </div>
        </div>
      </div>

      {/* ══════════════════ DESKTOP WALLET HERO ══════════════════ */}
      <div className="hidden md:block relative overflow-hidden rounded-2xl bg-gradient-to-br from-purple-600 to-purple-800 dark:from-purple-700 dark:to-purple-950 p-6 mb-5 shadow-lg">
        <WalletBackgroundWaves className="opacity-90" />
        <WalletIllustration className="absolute right-8 top-1/2 -translate-y-1/2 w-52 h-52 lg:w-60 lg:h-60" />

        <div className="relative z-10 flex items-start justify-between mb-5 gap-2">
          <div className="flex items-center gap-2">
            <p className="text-xs text-purple-200 uppercase tracking-wide font-medium">Wallet Balance</p>
            <button onClick={() => setBalanceVisible((v) => !v)} className="text-purple-200 hover:text-white transition">
              {balanceVisible ? <Eye size={14} /> : <EyeOff size={14} />}
            </button>
          </div>
          <div className="flex items-center gap-2">
            <CurrencyToggle onDark />
            <span className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-widest text-purple-100 bg-white/10 px-2.5 py-1 rounded-full">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
              Active
              <ShieldCheck size={11} className="text-emerald-300" />
            </span>
          </div>
        </div>

        <div className="relative z-10 max-w-[52%]">
          {walletLoading ? (
            <div className="h-9 w-40 rounded bg-white/15 animate-pulse mb-4" />
          ) : (
            <p className="text-3xl md:text-4xl font-bold text-white mb-4 break-words">
              {balanceVisible ? <Currency amount={walletBalance} /> : "••••••••"}
            </p>
          )}

          <div className="flex flex-wrap items-center gap-2">
            <Link
              to="/dashboard/wallet"
              className="inline-flex items-center gap-1.5 bg-purple-500 hover:bg-purple-400 text-white text-sm font-semibold px-4 py-2.5 rounded-xl transition shadow-sm"
            >
              <Plus size={15} /> Fund Wallet
            </Link>
            <Link
              to="/dashboard/transfer"
              className="inline-flex items-center gap-1.5 bg-white/15 hover:bg-white/25 text-white text-sm font-semibold px-4 py-2.5 rounded-xl transition"
            >
              <Send size={15} /> Transfer
            </Link>
          </div>
        </div>
      </div>

      {/* ══════════════════ MOBILE REFER & EARN BANNER ══════════════════ */}
      <Link to="/dashboard/referrals" className="md:hidden block mb-4">
        <div className="flex items-center gap-2.5 rounded-xl px-3 py-2.5 bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-900/15 dark:to-orange-900/10 border border-amber-200 dark:border-amber-800/50">
          <div className="w-9 h-9 rounded-lg bg-amber-400/90 flex items-center justify-center flex-shrink-0">
            <Gift className="w-4.5 h-4.5 text-white" size={18} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[12.5px] font-bold text-gray-900 dark:text-white leading-tight">Refer &amp; Earn</p>
            <p className="text-[10.5px] text-gray-500 dark:text-gray-400 leading-tight truncate">
              Invite friends and earn rewards on every referral.
            </p>
          </div>
          <span className="flex-shrink-0 flex items-center gap-1 bg-orange-500 text-white text-[10.5px] font-semibold px-2.5 py-1.5 rounded-lg">
            Invite Now <ArrowRight size={11} />
          </span>
        </div>
      </Link>

      {/* ══════════════════ DESKTOP REFER & EARN STRIP ══════════════════ */}
      <div className="hidden md:flex items-center gap-4 rounded-xl px-5 py-4 mb-6 bg-white dark:bg-[#12101f] border border-purple-200 dark:border-purple-900/60">
        <div className="w-12 h-12 rounded-xl bg-purple-100 dark:bg-purple-900/40 flex items-center justify-center flex-shrink-0">
          <Gift className="text-purple-600 dark:text-purple-300" size={22} />
        </div>

        <div className="flex-shrink-0 max-w-[210px]">
          <p className="text-[10px] font-bold uppercase tracking-wide text-purple-600 dark:text-purple-400 mb-0.5">
            Refer &amp; Earn
          </p>
          <p className="text-sm font-semibold text-gray-900 dark:text-white leading-snug">
            Invite friends. Earn rewards.
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
            Share your link and earn on every transaction.
          </p>
        </div>

        <div className="flex-1 flex items-center gap-2 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 min-w-0">
          <p className="text-xs text-gray-600 dark:text-gray-300 truncate flex-1">
            {referralLoading ? "Loading your link..." : referral?.referralLink || "Link unavailable — refresh to try again"}
          </p>
          <button
            onClick={() => referral && copy(referral.referralLink, "Referral link")}
            disabled={!referral}
            className="flex-shrink-0 p-1.5 rounded-md text-gray-400 hover:text-purple-600 hover:bg-purple-50 dark:hover:bg-purple-900/30 transition disabled:opacity-40"
          >
            <Copy size={14} />
          </button>
        </div>

        <Link
          to="/dashboard/referrals"
          className="flex-shrink-0 flex items-center gap-1.5 bg-purple-600 hover:bg-purple-700 text-white text-sm font-semibold px-4 py-2.5 rounded-xl transition"
        >
          Invite Now <ArrowRight size={14} />
        </Link>

        <div className="hidden lg:flex w-11 h-11 rounded-full bg-amber-100 dark:bg-amber-900/30 items-center justify-center flex-shrink-0">
          <Users2 className="text-amber-600 dark:text-amber-300" size={20} />
        </div>
      </div>

      {/* ══════════════════ MOBILE SERVICES LIST ══════════════════ */}
      <div className="md:hidden">
        <h2 className="text-[15px] font-bold text-gray-900 dark:text-white mb-2.5">Our Services</h2>
        <div className="space-y-2">
          {services.map((s) => (
            <ServiceRow key={s.title} s={s} />
          ))}
        </div>
      </div>

      {/* ══════════════════ DESKTOP QUICK ACCESS GRID ══════════════════ */}
      <div className="hidden md:block">
        <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-3">Quick Access</h2>
        <div className="grid grid-cols-3 lg:grid-cols-5 gap-4">
          {services.map((s) => (
            <ServiceCard key={s.title} s={s} />
          ))}
        </div>
      </div>
    </div>
  );
}